import { Fragment, memo, type ReactNode, useMemo, useState } from "react";

interface ThinkingTextProps {
  text: string;
}

interface ThinkingSection {
  key: string;
  heading: string;
  blocks: ThinkingContentBlock[];
}

type ThinkingContentBlock =
  | { type: "paragraph"; text: string }
  | { type: "code"; text: string };

const thinkingHeadingPattern = /^\s*\*\*(.+?)\*\*\s*$/;

function getThinkingHeading(line: string): string | null {
  const match = thinkingHeadingPattern.exec(line);
  const heading = match?.[1]?.trim();
  return heading ? heading : null;
}

function splitThinkingBlocks(lines: string[]): ThinkingContentBlock[] {
  const blocks: ThinkingContentBlock[] = [];
  let current: string[] = [];

  const flushParagraph = () => {
    if (current.length > 0) {
      blocks.push({ type: "paragraph", text: current.join("\n") });
      current = [];
    }
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      flushParagraph();
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length) {
        const codeLine = lines[index] ?? "";
        if (codeLine.trim().startsWith("```")) break;
        codeLines.push(codeLine);
        index += 1;
      }
      blocks.push({ type: "code", text: codeLines.join("\n") });
      continue;
    }

    if (line.startsWith("    ") || line.startsWith("\t")) {
      flushParagraph();
      const codeLines = [line.replace(/^( {4}|\t)/, "")];
      while (index + 1 < lines.length) {
        const nextLine = lines[index + 1] ?? "";
        if (!nextLine.startsWith("    ") && !nextLine.startsWith("\t")) break;
        codeLines.push(nextLine.replace(/^( {4}|\t)/, ""));
        index += 1;
      }
      blocks.push({ type: "code", text: codeLines.join("\n") });
      continue;
    }

    if (line.trim() === "") {
      flushParagraph();
      continue;
    }
    current.push(line);
  }
  flushParagraph();

  return blocks;
}

function parseThinkingOutline(text: string): ThinkingSection[] | null {
  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const firstHeading = getThinkingHeading(lines[0] ?? "");
  if (!firstHeading) return null;

  const sections: Array<{
    heading: string;
    bodyLines: string[];
  }> = [];

  let current: { heading: string; bodyLines: string[] } | null = null;

  const flush = () => {
    if (current) {
      sections.push(current);
      current = null;
    }
  };

  for (const line of lines) {
    const heading = getThinkingHeading(line);
    if (heading) {
      flush();
      current = { heading, bodyLines: [] };
      continue;
    }

    if (!current) continue;
    if (current.bodyLines.length === 0 && line.trim() === "") continue;
    current.bodyLines.push(line);
  }
  flush();

  return sections.map((section, index) => ({
    key: `${index}:${section.heading}`,
    heading: section.heading,
    blocks: splitThinkingBlocks(section.bodyLines),
  }));
}

function renderThinkingInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let index = 0;

  while (index < text.length) {
    const start = text.indexOf("`", index);
    if (start === -1) {
      nodes.push(text.slice(index));
      break;
    }

    const end = text.indexOf("`", start + 1);
    if (end === -1) {
      nodes.push(text.slice(index));
      break;
    }

    if (start > index) {
      nodes.push(text.slice(index, start));
    }

    const code = text.slice(start + 1, end);
    nodes.push(
      <code className="thinking-inline-code" key={`code-${start}`}>
        {code}
      </code>,
    );
    index = end + 1;
  }

  return nodes.map((node, nodeIndex) => (
    <Fragment key={typeof node === "string" ? `text-${nodeIndex}` : nodeIndex}>
      {node}
    </Fragment>
  ));
}

export const ThinkingText = memo(function ThinkingText({
  text,
}: ThinkingTextProps) {
  const outline = useMemo(() => parseThinkingOutline(text), [text]);
  const [closedSections, setClosedSections] = useState<Set<string>>(
    () => new Set(),
  );

  if (!outline) {
    return <span className="thinking-text thinking-text-plain">{text}</span>;
  }

  return (
    <div className="thinking-text thinking-outline">
      {outline.map((section) => {
        const isOpen = !closedSections.has(section.key);
        return (
          <details
            className="thinking-outline-section"
            key={section.key}
            open={isOpen}
            onToggle={(event) => {
              const nextOpen = event.currentTarget.open;
              setClosedSections((current) => {
                const next = new Set(current);
                if (nextOpen) {
                  next.delete(section.key);
                } else {
                  next.add(section.key);
                }
                return next;
              });
            }}
          >
            <summary className="thinking-outline-heading">
              <span className="thinking-outline-dot" aria-hidden="true" />
              <strong>{renderThinkingInline(section.heading)}</strong>
            </summary>
            {section.blocks.length > 0 && (
              <div className="thinking-outline-body">
                {section.blocks.map((block, index) => (
                  <Fragment key={index}>
                    {block.type === "code" ? (
                      <pre className="thinking-code-block">
                        <code>{block.text}</code>
                      </pre>
                    ) : (
                      <p>{renderThinkingInline(block.text)}</p>
                    )}
                  </Fragment>
                ))}
              </div>
            )}
          </details>
        );
      })}
    </div>
  );
});
